import { scaleTime, ScaleTime } from 'd3-scale';
import { utcFormat } from 'd3-time-format';
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';

import { useTimelineColors } from './timeline';

// Zoom viewport state
export interface ZoomState {
  // The visible portion of the full time domain (0-1 normalized)
  viewportStart: number;
  viewportEnd: number;
}

interface ZoomControlProps {
  // Full time domain of the data
  fullTimeDomain: [Date, Date];
  // Current zoom state
  zoomState: ZoomState;
  // Callback when zoom changes
  onZoomChange: (state: ZoomState) => void;
  // Width of the control (should match main chart width)
  width: number;
  // Margins to match main chart layout
  margin: { left: number; right: number };
}

// UTC formatters
const formatUtcTime = utcFormat('%H:%M');
const formatUtcDate = utcFormat('%b %d');

// Smart tick formatter: shows dates at midnight when spanning multiple days, times otherwise
const smartTickFormat = (domain: readonly [Date, Date]) => {
  const durationHours = (domain[1].getTime() - domain[0].getTime()) / (1000 * 60 * 60);
  const spansMultipleDays = durationHours > 24;

  return (tick: Date): string => {
    if (spansMultipleDays && tick.getUTCHours() === 0 && tick.getUTCMinutes() === 0) {
      return formatUtcDate(tick); // e.g. "Feb 08"
    }
    return formatUtcTime(tick); // e.g. "14:00"
  };
};

// Compute tick values at nice UTC intervals, adapting to zoom level
const computeUtcTicks = (timeScale: ScaleTime<number, number>): Date[] => {
  const domain = timeScale.domain();
  const start = domain[0];
  const end = domain[1];
  if (!start || !end) return [];

  const durationMs = end.getTime() - start.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);

  // Choose interval based on visible duration to get ~8-12 ticks
  // Intervals must divide 24 evenly for nice alignment: 1, 2, 3, 4, 6, 8, 12, 24
  let intervalHours: number;
  if (durationHours <= 6) {
    intervalHours = 1;
  } else if (durationHours <= 12) {
    intervalHours = 2;
  } else if (durationHours <= 24) {
    intervalHours = 3;
  } else if (durationHours <= 48) {
    intervalHours = 6;
  } else if (durationHours <= 96) {
    intervalHours = 12;
  } else {
    intervalHours = 24;
  }

  // Find first tick at interval boundary
  const startHour = start.getUTCHours();
  const firstTickHour = Math.ceil(startHour / intervalHours) * intervalHours;
  const firstTick = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), firstTickHour, 0, 0, 0),
  );
  if (firstTick < start) {
    firstTick.setUTCHours(firstTick.getUTCHours() + intervalHours);
  }

  const ticks: Date[] = [];
  const cursor = new Date(firstTick);
  while (cursor <= end) {
    ticks.push(new Date(cursor));
    cursor.setUTCHours(cursor.getUTCHours() + intervalHours);
  }
  return ticks;
};

export const ZoomControl: React.FC<ZoomControlProps> = ({ fullTimeDomain, zoomState, onZoomChange, width, margin }) => {
  const { colors } = useTimelineColors();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'viewport' | 'left' | 'right' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartState, setDragStartState] = useState<ZoomState | null>(null);

  const innerWidth = width - margin.left - margin.right;
  const controlHeight = 50;
  const miniMapHeight = 24;

  // Create scale for the overview
  const overviewScale = useMemo(
    () => scaleTime().domain(fullTimeDomain).range([0, innerWidth]),
    [fullTimeDomain, innerWidth],
  );

  // Calculate viewport position in pixels
  const viewportStartX = zoomState.viewportStart * innerWidth;
  const viewportEndX = zoomState.viewportEnd * innerWidth;
  const viewportWidth = viewportEndX - viewportStartX;

  // Minimum viewport width (prevents zooming in too much)
  const minViewportWidth = 50; // pixels
  const minViewportRatio = minViewportWidth / innerWidth;

  // Handle viewport drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'viewport' | 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(type);
      setDragStartX(e.clientX);
      setDragStartState({ ...zoomState });
    },
    [zoomState],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStartState) return;

      const deltaX = e.clientX - dragStartX;
      const deltaRatio = deltaX / innerWidth;

      let newStart = dragStartState.viewportStart;
      let newEnd = dragStartState.viewportEnd;

      if (isDragging === 'viewport') {
        // Pan the viewport
        const viewportSize = newEnd - newStart;
        newStart = Math.max(0, Math.min(1 - viewportSize, dragStartState.viewportStart + deltaRatio));
        newEnd = newStart + viewportSize;
      } else if (isDragging === 'left') {
        // Resize from left edge
        newStart = Math.max(0, Math.min(newEnd - minViewportRatio, dragStartState.viewportStart + deltaRatio));
      } else if (isDragging === 'right') {
        // Resize from right edge
        newEnd = Math.max(newStart + minViewportRatio, Math.min(1, dragStartState.viewportEnd + deltaRatio));
      }

      onZoomChange({ viewportStart: newStart, viewportEnd: newEnd });
    },
    [isDragging, dragStartState, dragStartX, innerWidth, minViewportRatio, onZoomChange],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    setDragStartState(null);
  }, []);

  // Add global mouse listeners when dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle scroll to zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Get mouse position relative to the timeline area
      const mouseX = e.clientX - rect.left - margin.left;
      const mouseRatio = Math.max(0, Math.min(1, mouseX / innerWidth));

      // Zoom factor (negative deltaY = zoom in, positive = zoom out)
      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;

      const currentWidth = zoomState.viewportEnd - zoomState.viewportStart;
      const newWidth = Math.max(minViewportRatio, Math.min(1, currentWidth * zoomFactor));

      // Calculate new viewport centered on mouse position
      // Preserve the relative position of the mouse within the viewport
      const mouseInViewport = (mouseRatio - zoomState.viewportStart) / currentWidth;
      let newStart = mouseRatio - mouseInViewport * newWidth;
      let newEnd = newStart + newWidth;

      // Clamp to bounds
      if (newStart < 0) {
        newStart = 0;
        newEnd = newWidth;
      }
      if (newEnd > 1) {
        newEnd = 1;
        newStart = 1 - newWidth;
      }

      onZoomChange({ viewportStart: newStart, viewportEnd: newEnd });
    },
    [zoomState, innerWidth, margin.left, minViewportRatio, onZoomChange],
  );

  // Handle click on minimap to pan to that location
  const handleMinimapClick = useCallback(
    (e: React.MouseEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickRatio = clickX / innerWidth;

      const viewportSize = zoomState.viewportEnd - zoomState.viewportStart;
      let newStart = clickRatio - viewportSize / 2;
      let newEnd = newStart + viewportSize;

      // Clamp to bounds
      if (newStart < 0) {
        newStart = 0;
        newEnd = viewportSize;
      }
      if (newEnd > 1) {
        newEnd = 1;
        newStart = 1 - viewportSize;
      }

      onZoomChange({ viewportStart: newStart, viewportEnd: newEnd });
    },
    [zoomState, innerWidth, onZoomChange],
  );

  // Reset zoom
  const handleResetZoom = useCallback(() => {
    onZoomChange({ viewportStart: 0, viewportEnd: 1 });
  }, [onZoomChange]);

  // Zoom percentage display
  const zoomPercentage = Math.round((1 / (zoomState.viewportEnd - zoomState.viewportStart)) * 100);
  const isZoomed = zoomState.viewportStart > 0.001 || zoomState.viewportEnd < 0.999;

  // Calculate visible time range
  const fullDuration = fullTimeDomain[1].getTime() - fullTimeDomain[0].getTime();
  const visibleStart = new Date(fullTimeDomain[0].getTime() + zoomState.viewportStart * fullDuration);
  const visibleEnd = new Date(fullTimeDomain[0].getTime() + zoomState.viewportEnd * fullDuration);

  // Generate time axis ticks for the overview - using UTC-aligned ticks
  const overviewTicks = useMemo(() => computeUtcTicks(overviewScale), [overviewScale]);

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: colors.background.row,
        borderTop: `1px solid ${colors.background.grid}`,
        padding: '8px 0',
        userSelect: 'none',
      }}
      onWheel={handleWheel}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          marginBottom: '6px',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: colors.text.primary,
            }}>
            Timeline Navigator
          </span>
          <span style={{ fontSize: '11px', color: colors.text.secondary }}>
            {formatUtcDate(visibleStart)} {formatUtcTime(visibleStart)} - {formatUtcDate(visibleEnd)}{' '}
            {formatUtcTime(visibleEnd)} UTC
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              fontSize: '11px',
              color: isZoomed ? '#1976D2' : colors.text.secondary,
              fontWeight: isZoomed ? 600 : 400,
            }}>
            {zoomPercentage}%
          </span>
          {isZoomed && (
            <button
              onClick={handleResetZoom}
              style={{
                padding: '3px 10px',
                fontSize: '11px',
                cursor: 'pointer',
                border: `1px solid ${colors.background.grid}`,
                borderRadius: '4px',
                backgroundColor: colors.background.rowAlt,
                color: colors.text.primary,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.background.grid;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.background.rowAlt;
              }}>
              Reset Zoom
            </button>
          )}
          <span style={{ fontSize: '10px', color: colors.text.secondary }}>
            Scroll here or Ctrl+scroll on charts • Drag to pan
          </span>
        </div>
      </div>

      <div
        style={{
          marginLeft: margin.left,
          marginRight: margin.right,
          position: 'relative',
          height: controlHeight,
        }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: innerWidth,
            height: miniMapHeight,
            backgroundColor: colors.background.rowAlt,
            borderRadius: '4px',
            border: `1px solid ${colors.background.grid}`,
            cursor: 'pointer',
          }}
          onClick={handleMinimapClick}>
          <svg
            width={innerWidth}
            height={miniMapHeight}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            {overviewTicks.map((tick, i) => {
              const x = overviewScale(tick);
              return (
                <line
                  key={i}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={miniMapHeight}
                  stroke={colors.background.grid}
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
              );
            })}
          </svg>
        </div>

        <div
          style={{
            position: 'absolute',
            top: 0,
            left: viewportStartX,
            width: Math.max(viewportWidth, minViewportWidth),
            height: miniMapHeight,
            backgroundColor: 'rgba(25, 118, 210, 0.2)',
            border: '2px solid var(--color-info-border-accent, #1976D2)',
            borderRadius: '4px',
            cursor: isDragging === 'viewport' ? 'grabbing' : 'grab',
            transition: isDragging ? 'none' : 'left 0.1s ease, width 0.1s ease',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'viewport')}>
          <div
            style={{
              position: 'absolute',
              left: -4,
              top: 0,
              width: 8,
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: 'transparent',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'left')}>
            <div
              style={{
                position: 'absolute',
                left: 3,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: 12,
                backgroundColor: 'var(--color-info-bg-accent-default, #1976D2)',
                borderRadius: '2px',
              }}
            />
          </div>

          <div
            style={{
              position: 'absolute',
              right: -4,
              top: 0,
              width: 8,
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: 'transparent',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'right')}>
            <div
              style={{
                position: 'absolute',
                right: 3,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: 12,
                backgroundColor: 'var(--color-info-bg-accent-default, #1976D2)',
                borderRadius: '2px',
              }}
            />
          </div>
        </div>

        <svg width={innerWidth} height={20} style={{ position: 'absolute', top: miniMapHeight + 4, left: 0 }}>
          {overviewTicks.map((tick, i) => {
            const x = overviewScale(tick);
            const tickFormat = smartTickFormat(fullTimeDomain);
            return (
              <g key={i}>
                <line x1={x} x2={x} y1={0} y2={4} stroke={colors.background.grid} strokeWidth={1} />
                <text x={x} y={16} textAnchor="middle" fontSize="9px" fill={colors.text.secondary}>
                  {tickFormat(tick)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// Helper to create a zoomed time scale from the full domain and zoom state
export const createZoomedTimeScale = (
  fullTimeDomain: [Date, Date],
  zoomState: ZoomState,
  innerWidth: number,
): ScaleTime<number, number> => {
  const fullDuration = fullTimeDomain[1].getTime() - fullTimeDomain[0].getTime();
  const zoomedStart = new Date(fullTimeDomain[0].getTime() + zoomState.viewportStart * fullDuration);
  const zoomedEnd = new Date(fullTimeDomain[0].getTime() + zoomState.viewportEnd * fullDuration);

  return scaleTime().domain([zoomedStart, zoomedEnd]).range([0, innerWidth]);
};

// Default zoom state (full view)
export const DEFAULT_ZOOM_STATE: ZoomState = {
  viewportStart: 0,
  viewportEnd: 1,
};
